import {
  Text,
  IconArrowUp,
  IconEllipsisHorizontal,
  IconPlus,
  IconAt,
  IconClipboard,
  Button,
  ContextControl,
  Pill,
  GreyGrey30,
  GreyGrey50,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  ShadowL,
  IndigoIndigo50,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useRef, useEffect, forwardRef, useState } from 'react'
import { ChatFileAttachment } from './ChatFileAttachment'
import { useArrowKeyNavigation } from './useArrowKeyNavigation'
import { useChatContextData } from './useChatContextData'
import { getTableSelectionLabel } from './helpers'

interface Props {
  placeholder?: string
  initialValue?: string
  value?: string
  onChangeValue?: (value: string) => void
  onSubmit?: (message: string) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
  sendDisabled?: boolean
  filters?: Array<{
    id: string
    label: string
    type: 'file' | 'page' | 'reference'
    removable?: boolean
  }>
  onRemoveFilter?: (id: string) => void
  hasAttachments?: boolean
  selectedContextItems?: Set<string>
  onContextItemsChange?: (selectedItems: Set<string>) => void
  revenueWaterfallData?: any
  tableSelection?: any
}

// Create stable default to prevent rerenders
const EMPTY_SET = new Set<string>()

export const ChatInputBox = ({
  placeholder = 'Ask AI...',
  initialValue = '',
  value: controlledValue,
  onChangeValue,
  onSubmit,
  onKeyDown,
  disabled = false,
  sendDisabled = false,
  hasAttachments = false,
  selectedContextItems = EMPTY_SET,
  onContextItemsChange,
  revenueWaterfallData,
  tableSelection
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(initialValue)

  // Update local state when initialValue changes (for external updates like table selection)
  useEffect(() => {
    // Only sync initialValue when uncontrolled
    if (controlledValue === undefined) {
      setValue(initialValue)
    }
  }, [initialValue, controlledValue])

  const handleRemoveContextItem = (contextValue: string) => {
    if (onContextItemsChange) {
      const newSet = new Set(selectedContextItems)
      newSet.delete(contextValue)
      onContextItemsChange(newSet)
    }
  }

  const navigationRefs = useArrowKeyNavigation(
    containerRef,
    handleRemoveContextItem
  )

  const isControlled = controlledValue !== undefined
  const displayValue = isControlled ? controlledValue! : value

  const handleChange = (newValue: string) => {
    if (isControlled) {
      onChangeValue?.(newValue)
    } else {
      setValue(newValue)
    }
  }

  const handleSend = () => {
    if (displayValue.trim() && onSubmit) {
      onSubmit(displayValue.trim())
      if (!isControlled) {
        setValue('')
      }
    }
  }

  const handleKeyDownWrapper = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
    onKeyDown?.(e)
  }

  return (
    <Flex
      ref={containerRef}
      backgroundColor="white"
      borderRadius="8px"
      width="100%"
      border="1px solid"
      borderColor={GreyGrey30}
      boxShadow={ShadowL}
      position="relative"
    >
      <Box width="100%" overflow="hidden">
        <Box minWidth="56px" width="100%" position="relative">
          <Box width="100%" height="100%" position="relative">
            <Flex
              direction="column"
              gap={2}
              align="flex-start"
              justify="flex-start"
              width="100%"
              p={2}
            >
              <ContextControls
                ref={navigationRefs.contextControl}
                selectedItems={selectedContextItems}
                onContextSelect={onContextItemsChange}
                contextPillsRef={navigationRefs.contextPills}
                onRemoveItem={handleRemoveContextItem}
                onContextClose={() => {
                  // focus textarea, with timeout to ensure context control is closed
                  // doesn't work without timeout :V
                  setTimeout(() => {
                    navigationRefs.textarea.current?.focus()
                  }, 100)
                }}
                revenueWaterfallData={revenueWaterfallData}
                tableSelection={tableSelection}
              />

              <ChatInputTextarea
                ref={navigationRefs.textarea}
                placeholder={placeholder}
                value={displayValue}
                onChange={handleChange}
                onKeyDown={handleKeyDownWrapper}
                disabled={disabled}
              />

              <ChatControls
                onSend={handleSend}
                disabled={sendDisabled || !value?.trim()}
                refs={{
                  attachmentButton: navigationRefs.attachmentButton,
                  moreButton: navigationRefs.moreButton,
                  sendButton: navigationRefs.sendButton
                }}
              />
            </Flex>
          </Box>
        </Box>

        {hasAttachments && (
          <Box
            width="100%"
            borderTop="1px solid"
            borderColor={GreyGrey30}
            position="relative"
            padding="8px"
          >
            <Flex>
              <ChatFileAttachment
                filename="This-is-a-file-title(1).pdf"
                size="24kb"
                type="PDF"
              />
            </Flex>
          </Box>
        )}
      </Box>
    </Flex>
  )
}

interface ContextControlsProps {
  selectedItems?: Set<string>
  onContextSelect?: (selectedItems: Set<string>) => void
  contextPillsRef?: React.MutableRefObject<HTMLButtonElement[]>
  onRemoveItem?: (value: string) => void
  onContextClose?: () => void
  revenueWaterfallData?: any
  tableSelection?: any
}

export const ContextControls = forwardRef<
  HTMLButtonElement,
  ContextControlsProps
>(
  (
    {
      selectedItems = new Set(),
      onContextSelect,
      contextPillsRef,
      onRemoveItem,
      onContextClose,
      revenueWaterfallData,
      tableSelection
    },
    ref
  ) => {
    const contextOptions = useChatContextData(
      false,
      revenueWaterfallData,
      tableSelection
    )
    const flattenedOptions = contextOptions.flatMap(option => [
      ...(option?.options || []),
      ...(option?.footerOptions || [])
    ])

    // Regular context items from useContextData
    const regularContextItems = Array.from(selectedItems)
      .filter(item => !item.startsWith('table-selection:'))
      .map(item => {
        const option = flattenedOptions.find(option => option.value === item)
        return option
      })
      .filter(Boolean)

    // External context items (like table selection)
    const externalContextItems = []

    // Add table selection item if it exists in selectedItems
    if (selectedItems.has('table-selection:current') && tableSelection) {
      externalContextItems.push({
        label: getTableSelectionLabel(tableSelection),
        value: 'table-selection:current',
        searchValues: ['table', 'selection', 'current'],
        secondaryLabel: `${tableSelection.rowCount} rows × ${tableSelection.columnCount} columns`,
        icon: <IconClipboard size={12} color="#F59E0B" />
      })
    }

    // Combine regular and external context items
    const selectedItemArray = [...regularContextItems, ...externalContextItems]

    const handleContextSelect = (selectedItem: string) => {
      const newItems = new Set(selectedItems)
      newItems.add(selectedItem)

      if (onContextSelect) {
        onContextSelect(newItems)
      }
    }

    const handleRemoveItem = (value: string) => {
      if (onRemoveItem) {
        onRemoveItem(value)
      }
    }

    // Update context pills ref when selected items change
    useEffect(() => {
      if (contextPillsRef) {
        const pillElements = selectedItemArray
          .map(
            (_, index) =>
              document.querySelector(
                `[data-context-pill-index="${index}"]`
              ) as HTMLButtonElement
          )
          .filter(Boolean)
        contextPillsRef.current = pillElements
      }
    }, [selectedItemArray, contextPillsRef])

    return (
      <Flex width="100%" gap={1} align="center" alignContent="center">
        <Flex gap={1} flexWrap="wrap" alignItems="flex-start" width="100%">
          <ContextControl
            ref={ref}
            icon={<IconAt color={GreyGrey70} />}
            placeholder="Search"
            options={contextOptions}
            onSelect={handleContextSelect}
            onUnselect={handleRemoveItem}
            labelWhenNoSelection="Add context"
            iconOnly={selectedItemArray.length > 0}
            onClose={onContextClose}
          />
          {selectedItemArray.length > 0 && (
            <Flex gap={1} flexWrap="wrap" flex={1} minWidth={0}>
              {selectedItemArray.map((item, index) => (
                <Flex
                  key={item?.value}
                  as="button"
                  tabIndex={0}
                  data-context-pill-index={index}
                  data-value={item?.value}
                  cursor="pointer"
                  _focus={{
                    outline: `2px solid ${IndigoIndigo50} !important`,
                    outlineOffset: '-1px'
                  }}
                  _focusVisible={{
                    outline: `2px solid ${IndigoIndigo50} !important`,
                    outlineOffset: '-1px'
                  }}
                  onClick={() => handleRemoveItem(item?.value || '')}
                  borderRadius="md"
                  flexShrink={0}
                >
                  <Pill
                    icon={
                      item && 'icon' in item
                        ? (item.icon as React.ReactNode)
                        : undefined
                    }
                    interactive
                  >
                    {item?.label}
                  </Pill>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      </Flex>
    )
  }
)

export const ChatInputTextarea = forwardRef<
  HTMLTextAreaElement,
  {
    placeholder: string
    value: string
    onChange: (value: string) => void
    onKeyDown?: (e: React.KeyboardEvent) => void
    disabled?: boolean
  }
>(({ placeholder, value, onChange, onKeyDown, disabled = false }, ref) => {
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const textareaRef =
    (ref as React.RefObject<HTMLTextAreaElement>) || internalRef
  const previousValue = useRef(value)

  // Keep textarea focused when value clears (message sent)
  useEffect(() => {
    if (previousValue.current && !value && textareaRef.current) {
      // Focus immediately since textarea is never disabled
      textareaRef.current.focus()
    }
    previousValue.current = value
  }, [value])

  // Focus when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [])

  return (
    <Flex width="100%" position="relative">
      <textarea
        ref={textareaRef}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        style={{
          width: '100%',
          height: 'auto',
          border: 'none',
          outline: 'none',
          resize: 'none',
          background: 'transparent',
          color: GreyGrey80,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
          ...Primary13Regular,
          // @ts-ignore
          '::placeholder': {
            color: GreyGrey50
          }
        }}
        onInput={e => {
          // Workaround for textarea height to be auto
          // we could use fieldSizing: 'content' but it's not supported by firefox
          const target = e.target as HTMLTextAreaElement
          target.style.height = '32px'
          target.style.height = `${target.scrollHeight}px`
        }}
      />
    </Flex>
  )
})

export const ChatControls = ({
  onSend,
  disabled = false,
  refs
}: {
  onSend?: () => void
  disabled?: boolean
  refs?: {
    attachmentButton: React.RefObject<HTMLButtonElement>
    moreButton: React.RefObject<HTMLButtonElement>
    sendButton: React.RefObject<HTMLButtonElement>
  }
}) => {
  return (
    <Flex
      width="100%"
      gap={1}
      align="center"
      alignContent="center"
      justifyContent="space-between"
    >
      <Flex gap={1}>
        <Button
          ref={refs?.attachmentButton}
          aria-label="Add attachment"
          variant="secondary"
          fullWidth
          size="24px"
          icon={<IconPlus />}
        />
        <Button
          ref={refs?.moreButton}
          aria-label="More options"
          variant="secondary"
          fullWidth
          size="24px"
          icon={<IconEllipsisHorizontal />}
        />
      </Flex>

      <Flex>
        <Button
          ref={refs?.sendButton}
          aria-label="Send message"
          variant="secondary"
          fullWidth
          size="24px"
          icon={<IconArrowUp />}
          onClick={onSend}
          disabled={disabled}
        />
      </Flex>
    </Flex>
  )
}
